import React, { useState } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    IconButton,
    Button,
    Drawer,
    TextField,
    Stack,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Autocomplete,
    Fab,
    Dialog,
    DialogContent,
    TablePagination
} from '@mui/material'
import {
    Add as AddIcon,
    FilterList as FilterIcon,
    Close as CloseIcon,
    CalendarToday as CalendarIcon,
    FileUpload as FileUploadIcon,
} from '@mui/icons-material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import DGRForm from './DGRForm/DGRForm'
import DGRDetail from './DGRDetail/DGRDetail'
import ImportDGRModal from './ImportDGRModal/ImportDGRModal'
import SolarPlantIcon from '../../../assets/images/solar plant_3.svg'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
const DGRMobileView = ({ logic }) => {
    const { state, setters, actions, permissions } = logic
    const { can } = permissions
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const {
        sortedReports, selectedReport, showForm, showDetail,
        editMode, selectedPlant, plants, plantLoading,
        selectedDate, importStatus, filterMode,
        startDate, endDate, importModalOpen
    } = state

    const {
        handleCreateReport, handleEditReport, handleViewReport,
        handleFormSubmit, handleFormClose,
        handleDetailClose, handleOpenImportModal, handleCloseImportModal, handleImportSuccess
    } = actions

    // Mobile Filter Drawer Content
    const FilterDrawer = () => (
        <Box sx={{ p: 2, width: '85vw', maxWidth: 350 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Filters</Typography>
                <IconButton onClick={() => setFilterDrawerOpen(false)}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
                <Box display="flex" justifyContent="center" mb={1}>
                    <ToggleButtonGroup
                        value={filterMode}
                        exclusive
                        onChange={(e, val) => val && setters.setFilterMode(val)}
                        size="small"
                    >
                        <ToggleButton value="date">
                            <CalendarIcon fontSize="small" sx={{ mr: 1 }} /> By Date
                        </ToggleButton>
                        <ToggleButton value="plant">
                            <img src={SolarPlantIcon} alt="plant" style={{ width: 18, height: 18, marginRight: 8 }} /> By Plant
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Autocomplete
                    size="small"
                    options={plants}
                    getOptionLabel={(option) => option.plantName || ''}
                    value={selectedPlant}
                    onChange={(event, newValue) => setters.setSelectedPlant(newValue)}
                    loading={plantLoading}
                    renderInput={(params) => (
                        <TextField {...params} label="Select Plant" placeholder="Plant Name" />
                    )}
                />

                {filterMode === 'date' ? (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Report Date"
                            value={selectedDate}
                            onChange={(date) => setters.setSelectedDate(date)}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </LocalizationProvider>
                ) : (
                    <>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="From"
                                value={startDate}
                                onChange={(date) => setters.setStartDate(date)}
                                maxDate={endDate}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                            <DatePicker
                                label="To"
                                value={endDate}
                                onChange={(date) => setters.setEndDate(date)}
                                minDate={startDate}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </LocalizationProvider>
                    </>
                )}

                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setFilterDrawerOpen(false)}
                    sx={{ mt: 2 }}
                >
                    Apply Filters
                </Button>
            </Stack>
        </Box>
    )

    return (
        <Box sx={{ pb: 10, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            {/* Mobile Header */}
            <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'white',
                p: 2,
                boxShadow: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography variant="h6" fontWeight="bold">DGR ({sortedReports.length})</Typography>
                <IconButton onClick={() => setFilterDrawerOpen(true)}>
                    <FilterIcon color="primary" />
                </IconButton>
            </Box>

            {/* Reports List */}
            <Box sx={{ p: 2 }}>
                {sortedReports
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((report) => (
                        <Card
                            key={report.id}
                            sx={{
                                mb: 2,
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                cursor: 'pointer'
                            }}
                            onClick={() => handleViewReport(report)}
                        >
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="caption" color="text.secondary">
                                        {dayjs(report.date || report.reportDate).format('DD MMM YYYY')}
                                    </Typography>
                                    {/* Performance Ratio Chip */}
                                    <Chip
                                        label={`PR: ${report.pr ? Number(report.pr).toFixed(1) + '%' : 'N/A'}`}
                                        size="small"
                                        color={report.pr < 75 ? "error" : "success"} // simple logic for color
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                </Box>
                                <Typography variant="subtitle1" fontWeight="600" lineHeight={1.3} mb={0.5}>
                                    {report.plantName}
                                </Typography>

                                <Box display="flex" justifyContent="space-between" mt={1}>
                                    <Box>
                                        <Typography variant="caption" display="block" color="text.secondary">Generation</Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {report.actGeneration ? Number(report.actGeneration).toFixed(0) : '-'} kWh
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" display="block" color="text.secondary">Yield</Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {report.actYield ? Number(report.actYield).toFixed(2) : '-'}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" display="block" color="text.secondary">GTI</Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {report.gti ? Number(report.gti).toFixed(0) : '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}

                {sortedReports.length === 0 && (
                    <Box textAlign="center" mt={4} color="text.secondary">
                        <Typography>No reports found.</Typography>
                    </Box>
                )}

                <TablePagination
                    component="div"
                    count={sortedReports.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10))
                        setPage(0)
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Rows:"
                    sx={{
                        borderTop: '1px solid #e0e0e0',
                        '& .MuiTablePagination-toolbar': {
                            px: { xs: 0.5, sm: 2 },
                            minHeight: 52,
                            width: '100%',
                            boxSizing: 'border-box',
                            flexWrap: { xs: 'nowrap', sm: 'nowrap' },
                            display: { xs: 'grid', sm: 'flex' },
                            gridTemplateColumns: { xs: 'auto 1fr auto', sm: 'unset' },
                            alignItems: 'center',
                        },
                        '& .MuiTablePagination-spacer': {
                            display: { xs: 'none', sm: 'block' },
                        },
                        '& .MuiTablePagination-selectLabel': {
                            display: { xs: 'none', sm: 'block' },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            mb: 0,
                        },
                        '& .MuiTablePagination-select': {
                            fontSize: { xs: '0.72rem', sm: '0.875rem' },
                            mr: { xs: 0.25, sm: 2 },
                            lineHeight: 1,
                        },
                        '& .MuiTablePagination-selectRoot': {
                            mr: 0,
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            gridColumn: { xs: '1', sm: 'unset' },
                            '&::before': {
                                content: '"Rows:"',
                                display: { xs: 'inline-block', sm: 'none' },
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                mr: 0.5,
                                whiteSpace: 'nowrap',
                                lineHeight: 1,
                            },
                        },
                        '& .MuiTablePagination-displayedRows': {
                            fontSize: { xs: '0.72rem', sm: '0.875rem' },
                            ml: { xs: 0, sm: 'auto' },
                            mr: { xs: 0.25, sm: 1 },
                            whiteSpace: 'nowrap',
                            textAlign: 'right',
                            gridColumn: { xs: '2', sm: 'unset' },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            lineHeight: 1,
                        },
                        '& .MuiTablePagination-actions': {
                            ml: 0,
                            flexShrink: 0,
                            gridColumn: { xs: '3', sm: 'unset' },
                            display: 'flex',
                            alignItems: 'center',
                            '& .MuiIconButton-root': {
                                padding: { xs: '1px', sm: '8px' },
                            }
                        }
                    }}
                />
            </Box>

            {/* Floating Action Buttons */}
            {can('dailyGeneration.import') && (
                <Fab
                    color="primary"
                    aria-label="import"
                    onClick={handleOpenImportModal}
                    size="medium"
                    sx={{
                        position: 'fixed',
                        bottom: 80,
                        right: 90, // 16px + 56px + 18px
                        zIndex: 1250
                    }}
                >
                    <FileUploadIcon />
                </Fab>
            )}

            {can('dailyGeneration.create') && (
                <Fab
                    color="primary"
                    aria-label="add"
                    onClick={handleCreateReport}
                    size="medium"
                    sx={{
                        position: 'fixed',
                        bottom: 80, // Moved up for MobileBottomNav
                        right: 16,
                        zIndex: 1250
                    }}
                >
                    <AddIcon />
                </Fab>
            )}

            {/* Filter Drawer */}
            <Drawer
                anchor="right"
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
            >
                <FilterDrawer />
            </Drawer>

            {/* Dialogs Reuse */}
            <Dialog
                open={showForm}
                onClose={handleFormClose}
                fullScreen
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f5f5f5' }}>
                    <Typography variant="h6">{editMode ? 'Edit Report' : 'New Report'}</Typography>
                    <IconButton onClick={handleFormClose}><CloseIcon /></IconButton>
                </Box>
                <DialogContent>
                    <DGRForm
                        report={selectedReport}
                        onSubmit={handleFormSubmit}
                        onCancel={handleFormClose}
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={showDetail}
                onClose={handleDetailClose}
                fullScreen
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f5f5f5' }}>
                    <Typography variant="h6">Details</Typography>
                    <IconButton onClick={handleDetailClose}><CloseIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    {selectedReport && (
                        <DGRDetail
                            report={selectedReport}
                            onEdit={() => {
                                handleDetailClose()
                                handleEditReport(selectedReport)
                            }}
                            onClose={handleDetailClose}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <ImportDGRModal
                open={importModalOpen}
                onClose={handleCloseImportModal}
                onImportSuccess={handleImportSuccess}
            />

            {/* Import Status Snackbar */}
            <Snackbar
                open={importStatus.open}
                autoHideDuration={6000}
                onClose={() => setters.setImportStatus({ ...importStatus, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setters.setImportStatus({ ...importStatus, open: false })}
                    severity={importStatus.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {importStatus.message}
                </Alert>
            </Snackbar>

        </Box>
    )
}

export default DGRMobileView
